import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Alert, Box, Link as MuiLink, TextField } from '@mui/material';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Heading,
  Section,
  Text,
} from '../components/atomic';
import { ContentPageLayout } from '../layouts';

/**
 * Sign In page component for RSM Collective
 *
 * Provides a fake authentication system for trust and professional appearance.
 * Always returns invalid credentials and redirects users to contact page for
 * password reset and signup requests.
 *
 * @returns {JSX.Element} Sign In page component
 */
function SignIn() {
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const _navigate = useNavigate();

  /**
   * Handle form input changes
   *
   * @param {Object} event - Input change event
   */
  const handleChange = (event) => {
    const { name, value } = event.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  /**
   * Handle form submission - always returns invalid credentials
   *
   * @param {Object} event - Form submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!credentials.email || !credentials.password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      setLoading(false);
      setError(
        'Invalid username or password. Please contact us for account access.'
      );
    }, 1500);
  };

  /**
   * Navigate to contact page for password reset or signup
   * Uses hard reload to avoid reCAPTCHA conflicts
   */
  const handleContactRedirect = () => {
    window.location.href = '/lets-collaborate#form';
  };

  return (
    <ContentPageLayout backgroundColor="var(--color-background-light)">
      <Section
        sx={{
          minHeight: 'calc(100vh - 84px)',
          py: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary)',
                mb: 3,
              }}
            >
              <PersonIcon
                sx={{ fontSize: 32, color: 'var(--color-primary-text)' }}
              />
            </Box>

            <Heading
              variant="h1"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '2rem',
                mb: 1,
              }}
            >
              Sign In
            </Heading>

            <Text sx={{ color: 'var(--color-text-muted)', fontSize: '1rem' }}>
              Access your RSM Collective account
            </Text>
          </Box>

          {/* Sign In Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: 'var(--color-card)',
              p: 4,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid var(--color-border)',
              position: 'relative',
              // Green corners
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -1,
                left: -1,
                width: '50px',
                height: '50px',
                borderTop: '3px solid var(--color-primary)',
                borderLeft: '3px solid var(--color-primary)',
                borderRadius: 0,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                right: -1,
                width: '50px',
                height: '50px',
                borderBottom: '3px solid var(--color-primary)',
                borderRight: '3px solid var(--color-primary)',
                borderRadius: 0,
              },
            }}
          >
            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  '& .MuiAlert-action': {
                    alignItems: 'center',
                    paddingLeft: 2,
                    paddingTop: 0,
                  },
                }}
                action={
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={handleContactRedirect}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'underline',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap',
                      minWidth: 'fit-content',
                    }}
                  >
                    Contact Us
                  </MuiLink>
                }
              >
                {error}
              </Alert>
            )}

            {/* Email Field */}
            <TextField
              name="email"
              type="email"
              label="Email Address"
              value={credentials.email}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'var(--color-primary)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--color-primary)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--color-text-muted)',
                  '&.Mui-focused': {
                    color: 'var(--color-primary)',
                  },
                },
              }}
            />

            {/* Password Field */}
            <TextField
              name="password"
              type="password"
              label="Password"
              value={credentials.password}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'var(--color-primary)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--color-primary)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--color-text-muted)',
                  '&.Mui-focused': {
                    color: 'var(--color-primary)',
                  },
                },
              }}
            />

            {/* Sign In Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-primary-text)',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderTopLeftRadius: 0,
                borderTopRightRadius: '12px',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: 0,
                '&:hover': {
                  backgroundColor: 'var(--color-primary-dark)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--color-text-muted)',
                },
              }}
            >
              {loading
                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon sx={{ fontSize: 20 }} />
                    Signing In...
                  </Box>
                : 'Sign In'}
            </Button>

            {/* Links Section */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Text
                sx={{
                  color: 'var(--color-text-muted)',
                  fontSize: '0.9rem',
                  mb: 2,
                }}
              >
                Need help accessing your account?
              </Text>

              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <MuiLink
                  href="/lets-collaborate#form"
                  sx={{
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot Password?
                </MuiLink>

                <MuiLink
                  href="/lets-collaborate#form"
                  sx={{
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Request Account
                </MuiLink>
              </Box>
            </Box>
          </Box>

          {/* Back to Home */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              component={Link}
              to="/"
              variant="outlined"
              sx={{
                color: 'var(--color-text-muted)',
                borderColor: 'var(--color-text-muted)',
                textTransform: 'none',
                fontWeight: 500,
                borderTopLeftRadius: 0,
                borderTopRightRadius: '12px',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: 0,
                '&:hover': {
                  backgroundColor:
                    'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-primary)',
                },
              }}
            >
              ← Back to Home
            </Button>
          </Box>
        </Container>
      </Section>
    </ContentPageLayout>
  );
}

export default SignIn;
